import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWizard } from '../useWizard';
import { WIZARD_STEPS, DAY_NAMES } from '../../../types/wizard';
import { ClinicStatus, ClinicStatusLabels, ClinicStatusColors } from '../../../types/clinic';
import { clinicApi } from '../../../services/clinicApi';
import { procedureApi } from '@features/procedures/services/procedureApi';

// "HH:mm" -> "h:mm AM/PM" (mirrors OperatingHoursForm's dropdown labels)
const formatTime = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${displayHour}:${String(m).padStart(2, '0')} ${ampm}`;
};

const ReviewStep: React.FC = () => {
  const { state, submitError, goToStep } = useWizard();

  const calculateInitialStatus = (): ClinicStatus => {
    if (state.basicInfo.name.toLowerCase().includes('do not use')) {
      return ClinicStatus.Inactive;
    }
    return ClinicStatus.Draft;
  };

  const initialStatus = calculateInitialStatus();

  // Lookup names for the selections made on the Capabilities step. Same query
  // keys as that step, so these are served from the cache the user just filled.
  const { data: categoryTypes } = useQuery({
    queryKey: ['categoryTypes'],
    queryFn: () => clinicApi.getCategoryTypes(),
    enabled: state.includeCapabilities,
  });
  const { data: capabilityTypes } = useQuery({
    queryKey: ['capabilityTypes'],
    queryFn: () => clinicApi.getCapabilityTypes(),
    enabled: state.includeCapabilities,
  });
  const { data: procedures } = useQuery({
    queryKey: ['procedures', 'mappedOnly'],
    queryFn: () => procedureApi.getProcedures(undefined, true),
    enabled: state.includeCapabilities,
  });

  const namesFor = (
    ids: number[],
    lookup: ReadonlyArray<{ id: number; name: string }> | undefined,
  ): string => {
    if (ids.length === 0) return 'None selected';
    if (!lookup) return `${ids.length} selected`;
    const byId = new Map(lookup.map((item) => [item.id, item.name]));
    return ids.map((id) => byId.get(id) ?? `#${id}`).join(', ');
  };

  const procedureLookup = procedures?.map((p) => ({ id: p.procedureId, name: p.effectiveName }));

  return (
    <div className="wizard-step review-step">
      <h2>Review & Submit</h2>
      <p className="step-description">Review the information below and click Submit to create the clinic.</p>

      {submitError && (
        <div className="alert alert-error">
          <strong>Error:</strong> {submitError}
        </div>
      )}

      {/* Initial Status Display */}
      <div className="review-section status-preview">
        <h3>Initial Status</h3>
        <div className="review-row">
          <span className="review-label">Status:</span>
          <span className={`review-value status-badge status-${ClinicStatusColors[initialStatus]}`}>
            {ClinicStatusLabels[initialStatus]}
          </span>
        </div>
        <p className="status-explanation">
          {initialStatus === ClinicStatus.Draft && 'Clinic will be created in Draft status.'}
          {initialStatus === ClinicStatus.Inactive && 'Clinic name contains "Do not use".'}
        </p>
      </div>

      {/* Basic Information Section */}
      <div className="review-section">
        <div className="review-section-header">
          <h3>Basic Information</h3>
          <button
            type="button"
            className="btn btn-sm btn-outline edit-section-btn"
            onClick={() => goToStep(WIZARD_STEPS.BASIC_INFO)}
          >
            Edit
          </button>
        </div>
        <div className="review-row">
          <span className="review-label">Clinic Name:</span>
          <span className="review-value">{state.basicInfo.name}</span>
        </div>
        <div className="review-row">
          <span className="review-label">Clinic Code:</span>
          <span className="review-value">Assigned automatically on save</span>
        </div>
        <div className="review-row">
          <span className="review-label">Address:</span>
          <span className="review-value">
            {state.basicInfo.address1}
            {state.basicInfo.address2 && <><br />{state.basicInfo.address2}</>}
            <br />{state.basicInfo.city}, {state.basicInfo.state} {state.basicInfo.zip}
          </span>
        </div>
        <div className="review-row">
          <span className="review-label">Phone:</span>
          <span className="review-value">{state.basicInfo.phone || 'Not set'}</span>
        </div>
        <div className="review-row">
          <span className="review-label">Fax:</span>
          <span className="review-value">{state.basicInfo.fax || 'Not set'}</span>
        </div>
        <div className="review-row">
          <span className="review-label">Email:</span>
          <span className="review-value">{state.basicInfo.email || 'Not set'}</span>
        </div>
        <div className="review-row">
          <span className="review-label">Website:</span>
          <span className="review-value">
            {state.basicInfo.website ? (
              <a href={state.basicInfo.website} target="_blank" rel="noopener noreferrer">
                {state.basicInfo.website}
              </a>
            ) : (
              'Not set'
            )}
          </span>
        </div>
      </div>

      {/* Operating Hours Section */}
      <div className="review-section">
        <div className="review-section-header">
          <h3>Operating Hours</h3>
          <button
            type="button"
            className="btn btn-sm btn-outline edit-section-btn"
            onClick={() => goToStep(WIZARD_STEPS.HOURS)}
          >
            Edit
          </button>
        </div>
        {state.operatingHours.map((hour) => (
          <div className="review-row" key={hour.dayOfWeek}>
            <span className="review-label">{DAY_NAMES[hour.dayOfWeek]}:</span>
            <span className="review-value">
              {hour.isClosed ? 'Closed' : `${formatTime(hour.openTime)} - ${formatTime(hour.closeTime)}`}
            </span>
          </div>
        ))}
      </div>

      {/* Capabilities & Services Section */}
      <div className="review-section">
        <div className="review-section-header">
          <h3>Capabilities &amp; Services</h3>
          {state.includeCapabilities && (
            <button
              type="button"
              className="btn btn-sm btn-outline edit-section-btn"
              onClick={() => goToStep(WIZARD_STEPS.CAPABILITIES)}
            >
              Edit
            </button>
          )}
        </div>
        {state.includeCapabilities ? (
          <>
            <div className="review-row">
              <span className="review-label">Categories:</span>
              <span className="review-value">{namesFor(state.categoryTypeIds, categoryTypes)}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Capabilities:</span>
              <span className="review-value">{namesFor(state.capabilityTypeIds, capabilityTypes)}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Services / Procedures:</span>
              <span className="review-value">{namesFor(state.serviceProcedureIds, procedureLookup)}</span>
            </div>
          </>
        ) : (
          <p className="not-set-message">Not set (optional)</p>
        )}
      </div>

      {/* Onboarding Section */}
      <div className="review-section">
        <div className="review-section-header">
          <h3>Onboarding</h3>
          {state.includeOnboarding && (
            <button
              type="button"
              className="btn btn-sm btn-outline edit-section-btn"
              onClick={() => goToStep(WIZARD_STEPS.ONBOARDING)}
            >
              Edit
            </button>
          )}
        </div>
        {state.includeOnboarding ? (
          <>
            {state.onboarding.preferredMethodOfAppointment && (
              <div className="review-row">
                <span className="review-label">Preferred Appointment Method:</span>
                <span className="review-value">{state.onboarding.preferredMethodOfAppointment}</span>
              </div>
            )}
            {state.onboarding.electronicChainOfCustody && (
              <div className="review-row">
                <span className="review-label">Electronic Chain of Custody:</span>
                <span className="review-value">{state.onboarding.electronicChainOfCustody}</span>
              </div>
            )}
            {state.onboarding.reportingMethod && (
              <div className="review-row">
                <span className="review-label">Reporting Method:</span>
                <span className="review-value">{state.onboarding.reportingMethod}</span>
              </div>
            )}
            <div className="review-row">
              <span className="review-label">DOT Certification:</span>
              <span className="review-value">
                {state.onboarding.dotCertification ? `Yes (${state.onboarding.numberOfCertifiedDOTProfessionals} professionals)` : 'No'}
              </span>
            </div>
            <div className="review-row">
              <span className="review-label">W-9 On File:</span>
              <span className="review-value">{state.onboarding.w9OnFile ? 'Yes' : 'No'}</span>
            </div>
            {state.onboarding.eScreenCode && (
              <div className="review-row">
                <span className="review-label">eScreen Code:</span>
                <span className="review-value">{state.onboarding.eScreenCode}</span>
              </div>
            )}
            {state.onboarding.formFoxCode && (
              <div className="review-row">
                <span className="review-label">FormFox Code:</span>
                <span className="review-value">{state.onboarding.formFoxCode}</span>
              </div>
            )}
            {state.onboarding.questCode && (
              <div className="review-row">
                <span className="review-label">Quest Code:</span>
                <span className="review-value">{state.onboarding.questCode}</span>
              </div>
            )}
          </>
        ) : (
          <p className="not-set-message">Not set (optional)</p>
        )}
      </div>

      {/* Contact Section */}
      <div className="review-section">
        <div className="review-section-header">
          <h3>Primary Contact</h3>
          {state.includeContact && (
            <button
              type="button"
              className="btn btn-sm btn-outline edit-section-btn"
              onClick={() => goToStep(WIZARD_STEPS.CONTACT)}
            >
              Edit
            </button>
          )}
        </div>
        {state.includeContact ? (
          <>
            <div className="review-row">
              <span className="review-label">Name:</span>
              <span className="review-value">{state.contact.name || 'Not set'}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Phone:</span>
              <span className="review-value">{state.contact.phone || 'Not set'}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Email:</span>
              <span className="review-value">{state.contact.email || 'Not set'}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Role:</span>
              <span className="review-value">{state.contact.role || 'Not set'}</span>
            </div>
          </>
        ) : (
          <p className="not-set-message">Not set (optional)</p>
        )}
      </div>
    </div>
  );
};

export default ReviewStep;
